import { useModal } from 'modules/modal/hooks/useModal';
import { OnboardingModeEnum } from 'modules/onboarding/types/onboarding-mode.enum';
import { useToast } from 'modules/toast/hooks/useToast';
import {
  extractionError,
  extractionInProgress,
  extractionInProgressToastId,
  extractionSuccess,
} from 'modules/toast/toastDictionary';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { realtimerAudiences } from 'realtimer/realtimerAudiences';
import { useExpertHubStateLazyQuery } from 'shared/hooks/api/expertHubState/expertHubState.generated';
import { useAuthStore } from 'shared/hooks/stores/useAuthStore';
import { useOnboardingStore } from 'shared/hooks/stores/useOnboardingStore';
import { useOnboardingMode } from 'shared/hooks/useOnboardingMode';
import { useRealtimer } from 'shared/hooks/useRealtimer';
import { ExtractionStateEnum } from 'types/enums/extraction-state.enum';
import { useShallow } from 'zustand/react/shallow';
import { ExtractionErrorModal } from '../modals/ExtractionErrorModal';
import { ExtractionSuccessModal } from '../modals/ExtractionSuccessModal';

export const useExtractionNotifications = () => {
  const { t } = useTranslation();
  const { displayToast, dismissToast } = useToast();
  const { openModal, isAnyModalDisplayed } = useModal();

  const { onboardingMode } = useOnboardingMode();
  const expertId = useAuthStore((state) => state.user?.expertId)!;
  const { extractionState, finishedAt, loadOnboardingData } = useOnboardingStore(
    useShallow((state) => ({
      extractionState: state.extractionState,
      finishedAt: state.finishedAt,
      loadOnboardingData: state.loadOnboardingData,
    })),
  );

  const [getExpertHubState] = useExpertHubStateLazyQuery({
    fetchPolicy: 'no-cache',
  });

  const handleRealtimerHubStateUpdate = useCallback(async () => {
    const res = await getExpertHubState();
    const { expertHubState } = res.data ?? {};

    if (!expertHubState) {
      return;
    }
    const updatedExtractionState = expertHubState.extractionState as unknown as ExtractionStateEnum;
    if (updatedExtractionState === ExtractionStateEnum.Completed) {
      dismissToast(extractionInProgressToastId);
      openModal('extractionSuccessModal', (closeModal) => (
        <ExtractionSuccessModal onClose={closeModal} />
      ));
    }
    if (updatedExtractionState === ExtractionStateEnum.Error) {
      dismissToast(extractionInProgressToastId);
      openModal('extractionErrorModal', (closeModal) => (
        <ExtractionErrorModal onClose={closeModal} />
      ));
    }
    loadOnboardingData(expertHubState);
  }, [loadOnboardingData, getExpertHubState]);

  useRealtimer(realtimerAudiences.hubStateUpdated(expertId), handleRealtimerHubStateUpdate);

  const displayExtractionToast = useCallback(() => {
    if (extractionState === ExtractionStateEnum.InProgress) {
      displayToast(extractionInProgress(t));
    }
    if (extractionState === ExtractionStateEnum.Completed) {
      displayToast(extractionSuccess(t));
    }
    if (extractionState === ExtractionStateEnum.Error) {
      displayToast(extractionError(t));
    }
  }, [displayToast, extractionState]);

  useEffect(() => {
    if (onboardingMode === OnboardingModeEnum.Manual || finishedAt || isAnyModalDisplayed) {
      return;
    }
    displayExtractionToast();
  }, []);
};
